// Task 6.0ag. What survives an ended session, and how a sign-in decides
// whether to give it back.
//
// When a session ends (refresh token expired or rejected, or a live-tab
// forced logout) the user is sent to /login and returns to the page they were
// on after signing in. Offline mode keeps its snapshot in sessionStorage; the
// owner chose to keep it while the login page is up so the round trip lands
// back in offline mode -- but only for the account that entered it, or a
// different account signing in on the same tab could read the previous
// account's snapshot.

export const OFFLINE_MODE_KEY = 'offlineMode';
export const OFFLINE_SNAPSHOT_KEY = 'offlineSnapshot';
export const OFFLINE_USER_KEY = 'offlineCanvasUserId';
// The signed-in email that entered offline mode. `clearTokens()` removes
// `userEmail` from localStorage, so without this copy "the same account" is
// unknowable once the session has ended.
export const OFFLINE_OWNER_KEY = 'offlineOwnerEmail';
// Where to go after signing in again. In sessionStorage rather than only
// router state because the live-tab forced logout hard-redirects with
// `window.location.href`, which cannot carry state.
export const RETURN_PATH_KEY = 'authReturnPath';

export const OFFLINE_DISCARDED_EVENT = 'canvas:offline-session-discarded';

/** Every key an ended session must not wipe while offline mode is on. */
export const OFFLINE_SESSION_KEYS = [
  OFFLINE_MODE_KEY,
  OFFLINE_SNAPSHOT_KEY,
  OFFLINE_USER_KEY,
  OFFLINE_OWNER_KEY,
];

const normaliseEmail = (email: unknown): string =>
  typeof email === 'string' ? email.trim().toLowerCase() : '';

// Internal paths only: "//host" and "https://..." are not places this app
// sends a user, and /login itself would be a loop.
export const isSafeReturnPath = (path: unknown): path is string =>
  typeof path === 'string' &&
  path.startsWith('/') &&
  !path.startsWith('//') &&
  !path.startsWith('/\\') &&
  !path.startsWith('/login');

export const rememberReturnPath = (path: string): void => {
  if (!isSafeReturnPath(path)) return;
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, path);
  } catch {
    // Losing the return path only costs the user a click.
  }
};

/** Read and remove the remembered page (consumed once). */
export const takeReturnPath = (): string | null => {
  const path = sessionStorage.getItem(RETURN_PATH_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);
  return isSafeReturnPath(path) ? path : null;
};

export const isOfflineSession = (): boolean =>
  sessionStorage.getItem(OFFLINE_MODE_KEY) === 'true';

/**
 * True when `email` is the account that entered the offline session on this
 * tab. An offline session with no recorded owner is treated as someone else's.
 */
export const isOfflineOwner = (email: unknown): boolean => {
  const owner = normaliseEmail(sessionStorage.getItem(OFFLINE_OWNER_KEY));
  return owner !== '' && owner === normaliseEmail(email);
};

/**
 * Call on EVERY path that sets a user, before it is set: login, the
 * new-password challenge, registration's auto sign-in, the on-load restore
 * from localStorage (shared across tabs), and anything added later. If an
 * offline session is on this tab and `email` is not the account that entered
 * it, the previous account's offline keys and remembered page are removed, so
 * no page can render that snapshot for a different account. Returns true when
 * it discarded something.
 *
 * An offline session with no recorded owner is discarded too: it cannot be
 * told apart from another account's, so nothing adopts it. The only writer of
 * an owner is AppModeContext.activateOfflineMode.
 *
 * Not for a signed-in user who merely reaches /login: that is not a sign-in.
 */
export const discardOfflineSessionUnlessOwner = (email: unknown): boolean => {
  if (!isOfflineSession() || isOfflineOwner(email)) return false;
  for (const key of OFFLINE_SESSION_KEYS) sessionStorage.removeItem(key);
  sessionStorage.removeItem(RETURN_PATH_KEY);
  // AuthProvider sits outside AppModeProvider; the event is how the in-memory
  // mode hears about it, in the same batch as the new user.
  window.dispatchEvent(new Event(OFFLINE_DISCARDED_EVENT));
  return true;
};

/**
 * Remember the current page, then empty sessionStorage except what the round
 * trip needs: the return path always, and the offline keys while offline mode
 * is on. Everything else (Canvas token, selected child, guest state) goes.
 */
export const clearSessionKeepingReturn = (): void => {
  const { pathname, search, hash } = window.location;
  rememberReturnPath(`${pathname}${search}${hash}`);

  const keys = isOfflineSession()
    ? [RETURN_PATH_KEY, ...OFFLINE_SESSION_KEYS]
    : [RETURN_PATH_KEY];
  const kept: Array<[string, string]> = [];
  for (const key of keys) {
    const value = sessionStorage.getItem(key);
    if (value !== null) kept.push([key, value]);
  }

  sessionStorage.clear();
  for (const [key, value] of kept) sessionStorage.setItem(key, value);
};
